<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- mouse handling differs from key handling. both will be used -->
<template>
  <div
    tabindex="-1"
    role="menu"
    ref="cat"
    class="mmm-nav-sub-menu-cat"
    @mouseenter="updateSubMenu(index + 1)"
    @mouseleave="updateSubMenu(-1)"
  >
    <a
      :href="item.path"
      role="menuitem"
      ref="catTitle"
      class="mmm-nav-sub-menu-cat__link"
      @mousedown="handleMouseDown"
    >
      <span class="mmm-nav-sub-menu-cat__title">{{ sanitize(item.title) }}</span>
    </a>
    <div v-if="item.children.length > 0" class="mmm-nav-sub-menu-cat__children">
      <NavItemDesktop
        :path="[...props.path, i]"
        @update-selection="handleUpdateSelection"
        :subMenuSelection="subMenuSelection"
        :is-keyboard-active="isKeyboardActive"
        :index="i"
        :parent-index="index"
        :item="child"
        v-for="(child, i) in item.children"
        :key="child.path"
        :ref="
          el => {
            if (el && typeof el === 'object' && 'handleKeyLeft' in el) {
              items[i] = el as unknown as INavItemDesktopComponent;
            } else {
              items[i] = null;
            }
          }
        "
        :depth="props.depth"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';

import { sanitize, type ISubHeaderNavItem } from '../helper';

import NavItemDesktop, { type INavItemDesktopComponent } from './NavItemDesktop.ce.vue';

const cat = ref<HTMLElement>();
const catTitle = ref<HTMLElement>();
const items = ref<(INavItemDesktopComponent | null)[]>([]);

const props = defineProps<{
  item: ISubHeaderNavItem;
  index: number;
  depth: number;
  subMenuSelection: number[];
  path: number[];
  isKeyboardActive: boolean;
}>();

let currentIndex = -1;
function handleMouseDown(event: MouseEvent) {
  event.stopPropagation();
}

function handleEscape() {
  if (props.subMenuSelection.length === 3) {
    handleKeyLeft();
  } else {
    const index = props.subMenuSelection[2];
    if (index >= 0) {
      items.value[index]?.handleEscape();
    }
  }
}

function handleKeyLeft() {
  if (props.subMenuSelection.length === 3) {
    const tmp = [...props.subMenuSelection];
    tmp.pop();
    emit('updateSelection', tmp);
    currentIndex = -1;
  } else {
    const index = props.subMenuSelection[2];
    if (index >= 0) {
      items.value[index]?.handleKeyLeft();
    }
  }
}

function handleKeyRight() {
  if (props.subMenuSelection.length >= 3 && currentIndex >= 0) {
    items.value[currentIndex]?.handleKeyRight();
  }
}

function handleKeyUp() {
  if (props.subMenuSelection.length === 2) {
    currentIndex = items.value.length - 1;
    const temp = [...props.subMenuSelection, currentIndex];
    emit('updateSelection', temp);
    catTitle.value?.focus();
  } else if (props.subMenuSelection.length === 3) {
    currentIndex--;
    if (currentIndex < -1) {
      currentIndex = items.value.length - 1;
    }
    focusElementByIndex(currentIndex);
  } else {
    items.value[currentIndex]?.handleKeyUp();
  }
}

function handleKeyDown() {
  if (props.subMenuSelection.length === 2) {
    const temp = [...props.subMenuSelection, currentIndex];
    emit('updateSelection', temp);
    catTitle.value?.focus();
  } else if (props.subMenuSelection.length === 3) {
    currentIndex++;
    if (currentIndex > items.value.length - 1) {
      currentIndex = -1;
    }
    focusElementByIndex(currentIndex);
  } else {
    items.value[currentIndex]?.handleKeyDown();
  }
}

function focus() {
  if (props.item.children.length === 0) {
    catTitle.value?.focus();
  } else {
    cat.value?.focus();
  }
}

function focusElementByIndex(index: number) {
  if (index === -1) {
    catTitle.value?.focus();
  } else {
    const item = items.value[index];
    if (item) {
      item.focus();
    }
  }

  const temp = [...props.subMenuSelection];
  temp[temp.length - 1] = index;
  emit('updateSelection', temp);
}

function handleUpdateSelection(selection: number[]) {
  emit('updateSelection', selection);
}

function updateSubMenu(index: number) {
  if (props.isKeyboardActive) return;
  if (index === -1) {
    emit('updateSelectionWithoutFocus', [props.subMenuSelection[0]]);
  } else {
    if (props.subMenuSelection.length < 3) {
      const tmp = [...props.subMenuSelection];
      tmp[1] = index;
      emit('updateSelectionWithoutFocus', tmp);
    }
  }
}

const emit = defineEmits(['updateSelection', 'updateSelectionWithoutFocus']);

defineExpose({
  handleKeyLeft,
  handleKeyRight,
  handleKeyUp,
  handleKeyDown,
  handleEscape,
  focus,
});

export interface INavSubMenuCategoryDesktopComponent {
  handleKeyLeft(): void;
  handleKeyRight(): void;
  handleKeyUp(): void;
  handleKeyDown(): void;
  handleEscape(): void;
  focus(): void;
}
</script>

<style lang="scss" scoped>
.mmm-nav-sub-menu-cat {
  padding-right: 16px;

  &__link {
    font-size: 20px;
    color: var(--default-text-color);
    text-decoration: none;
    font-weight: bold;

    &:focus,
    &:hover {
      text-decoration: underline;
    }
  }

  &__children {
    padding: 10px 0 0;
    margin: 0;
    list-style-type: none;
    pointer-events: none;

    > * {
      pointer-events: all;
    }
  }
}
</style>
